import type { CollectionResponse, LibraryGridItem } from './types';

/** Minimal document shape needed for name filtering (avoids Chat coupling). */
export type LibraryDocumentName = {
  filename: string;
};

/**
 * Normalize text for library name search: trim, lowercase, and NFKC so IME
 * input (NFC) matches macOS/API-stored names (often NFD) for Japanese, etc.
 */
export function normalizeLibrarySearchText(value: string): string {
  return value.trim().toLowerCase().normalize('NFKC');
}

/** Normalize a search query for case-insensitive substring matching. */
export function normalizeLibrarySearchQuery(query: string): string {
  return normalizeLibrarySearchText(query);
}

/**
 * Filter library grid items (collections + legacy source lists) by name.
 * Empty/whitespace queries return the original list unchanged.
 */
export function filterGridItemsByName(
  items: LibraryGridItem[],
  query: string
): LibraryGridItem[] {
  const normalized = normalizeLibrarySearchQuery(query);
  if (!normalized) return items;
  return items.filter((item) =>
    normalizeLibrarySearchText(item.item.name).includes(normalized)
  );
}

/**
 * Filter collection responses by name.
 * Empty/whitespace queries return the original list unchanged.
 */
export function filterCollectionsByName(
  collections: CollectionResponse[],
  query: string
): CollectionResponse[] {
  const normalized = normalizeLibrarySearchQuery(query);
  if (!normalized) return collections;
  return collections.filter((c) =>
    normalizeLibrarySearchText(c.name).includes(normalized)
  );
}

/**
 * Filter library documents/jobs by filename.
 * Empty/whitespace queries return the original list unchanged.
 */
export function filterDocumentsByName<T extends LibraryDocumentName>(
  items: T[],
  query: string
): T[] {
  const normalized = normalizeLibrarySearchQuery(query);
  if (!normalized) return items;
  return items.filter((item) =>
    normalizeLibrarySearchText(item.filename).includes(normalized)
  );
}
